import React, { useState, useMemo, useEffect, useRef } from 'react';
import { router } from '@inertiajs/react';
import StaffLayout from '@/layouts/StaffLayout';
import ThemeWrapper from '@/components/ThemeWrapper';
import Avatar from '@/components/common/Avatar';
import useRoutes from '@/hooks/useRoutes';
import { Search, X, ChevronLeft, ChevronRight, FileCheck, Eye, Edit, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

interface Course {
    id: number;
    title: string;
}

interface Section {
    id: number;
    title: string;
    course_id: number;
    course__title: string;
}

interface Topic {
    id: number;
    title: string;
    section_id: number;
    section__title: string;
    section__course_id: number;
    section__course__title: string;
}

interface SubmissionData {
    id: number;
    status: 'pending' | 'approved' | 'rejected';
    created_at: string;
    reviewed_at: string | null;
    assignment: {
        id: number;
        title: string;
        course: { id: number; title: string };
        section: { id: number; title: string } | null;
        topic: { id: number; title: string } | null;
    };
    student: {
        id: number;
        full_name: string;
        email: string;
        avatar_url: string | null;
    };
    reviewed_by: {
        id: number;
        full_name: string;
    } | null;
    teacher_comment: string;
    url_1: string | null;
    url_2: string | null;
    evidence_url: string | null;
}

interface Pagination {
    page: number;
    per_page: number;
    total: number;
    num_pages: number;
    has_previous: boolean;
    has_next: boolean;
    previous_page: number | null;
    next_page: number | null;
}

interface AssignmentSubmissionsIndexProps {
    submissions: SubmissionData[];
    courses: Course[];
    sections: Section[];
    topics: Topic[];
    pagination: Pagination;
    sort: {
        by: SortKey;
        direction: 'asc' | 'desc';
    };
    filters: {
        search: string;
        course: string;
        section: string;
        topic: string;
        status: string;
    };
}

type SortKey =
    | 'student'
    | 'assignment'
    | 'course'
    | 'section'
    | 'status'
    | 'created_at'
    | 'reviewed_at';

const IndexContent: React.FC<AssignmentSubmissionsIndexProps> = ({
    submissions,
    courses,
    sections,
    topics,
    pagination,
    sort,
    filters,
}) => {
    const { route } = useRoutes();
    const [searchQuery, setSearchQuery] = useState(filters.search);
    const [localCourse, setLocalCourse] = useState(filters.course);
    const [localSection, setLocalSection] = useState(filters.section);
    const [localTopic, setLocalTopic] = useState(filters.topic);
    const [localStatus, setLocalStatus] = useState(filters.status);
    const isFirstRender = useRef(true);
    const skipNextSearchEffect = useRef(false);
    const currentSortBy = sort?.by || 'created_at';
    const currentSortDirection = sort?.direction || 'desc';

    // Cascading filter: sections filtered by selected course
    const availableSections = useMemo(() => {
        if (!localCourse) return sections;
        const courseId = Number(localCourse);
        return sections.filter((section) => section.course_id === courseId);
    }, [sections, localCourse]);

    // Cascading filter: topics filtered by selected section or course
    const availableTopics = useMemo(() => {
        if (localSection) {
            const sectionId = Number(localSection);
            return topics.filter((topic) => topic.section_id === sectionId);
        }
        if (localCourse) {
            const courseId = Number(localCourse);
            return topics.filter((topic) => topic.section__course_id === courseId);
        }
        return topics;
    }, [topics, localSection, localCourse]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (skipNextSearchEffect.current) {
            skipNextSearchEffect.current = false;
            return;
        }
        const timer = setTimeout(() => {
            applyFilters({ search: searchQuery, page: 1 });
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const applyFilters = (overrides: Record<string, string | number>) => {
        router.get(
            route('staff-assignment-submissions').toString(),
            {
                page: 1,
                sort_by: currentSortBy,
                sort_direction: currentSortDirection,
                search: searchQuery,
                course: filters.course,
                section: filters.section,
                topic: filters.topic,
                status: filters.status,
                ...overrides,
            },
            { preserveState: true, preserveScroll: true }
        );
    };

    const handleCourseChange = (value: string) => {
        setLocalCourse(value);
        setLocalSection('');
        setLocalTopic('');
        applyFilters({ course: value, section: '', topic: '' });
    };

    const handleSectionChange = (value: string) => {
        setLocalSection(value);
        setLocalTopic('');
        applyFilters({ section: value, topic: '' });
    };

    const handleTopicChange = (value: string) => {
        setLocalTopic(value);
        applyFilters({ topic: value });
    };

    const handleStatusChange = (value: string) => {
        setLocalStatus(value);
        applyFilters({ status: value });
    };

    const handleSearchClear = () => {
        skipNextSearchEffect.current = true;
        setSearchQuery('');
        applyFilters({ search: '', page: 1 });
    };

    const handleSort = (key: SortKey) => {
        const nextDirection =
            currentSortBy === key
                ? (currentSortDirection === 'asc' ? 'desc' : 'asc')
                : (key === 'created_at' || key === 'reviewed_at' ? 'desc' : 'asc');

        router.get(
            route('staff-assignment-submissions').toString(),
            {
                page: 1,
                sort_by: key,
                sort_direction: nextDirection,
                search: searchQuery,
                course: filters.course,
                section: filters.section,
                topic: filters.topic,
                status: filters.status,
            },
            {
                preserveState: true,
                preserveScroll: true,
            }
        );
    };

    const renderSortIcon = (key: SortKey) => {
        if (currentSortBy !== key) return <ArrowUpDown size={14} className="text-gray-400" />;
        if (currentSortDirection === 'asc') return <ArrowUp size={14} className="text-gray-600" />;
        return <ArrowDown size={14} className="text-gray-600" />;
    };

    const handlePageChange = (page: number) => {
        router.get(
            route('staff-assignment-submissions').toString(),
            {
                page,
                sort_by: currentSortBy,
                sort_direction: currentSortDirection,
                search: searchQuery,
                course: filters.course,
                section: filters.section,
                topic: filters.topic,
                status: filters.status,
            },
            {
                preserveState: true,
                preserveScroll: true,
            }
        );
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'approved':
                return (
                    <span className="inline-flex rounded-full bg-green-100 px-2 text-xs font-semibold leading-5 text-green-800">
                        Aprobado
                    </span>
                );
            case 'rejected':
                return (
                    <span className="inline-flex rounded-full bg-red-100 px-2 text-xs font-semibold leading-5 text-red-800">
                        No aprobado
                    </span>
                );
            case 'pending':
            default:
                return (
                    <span className="inline-flex rounded-full bg-yellow-100 px-2 text-xs font-semibold leading-5 text-yellow-800">
                        Pendiente
                    </span>
                );
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    return (
        <StaffLayout>
            <div className="mx-auto max-w-7xl">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">
                        Entrega de tareas
                    </h1>
                    <p className="mt-2 text-gray-600">
                        Gestión de entregas de tareas de estudiantes
                    </p>
                </div>

                {/* Search and Filters */}
                <div className="mb-6 flex flex-col gap-4">
                    {/* Search Bar */}
                    <div className="relative flex-1">
                        <Search
                            className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400"
                            size={18}
                        />
                        <input
                            type="text"
                            placeholder="Buscar por nombre o email del estudiante..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-10 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                onClick={handleSearchClear}
                                className="absolute right-3 top-1/2 -translate-y-1/2 transform text-gray-400 hover:text-gray-600"
                                aria-label="Limpiar búsqueda"
                            >
                                <X size={18} />
                            </button>
                        )}
                    </div>

                    {/* Filters Row */}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {/* Course Filter */}
                        <select
                            value={localCourse}
                            onChange={(e) => handleCourseChange(e.target.value)}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                            <option value="">Todos los cursos</option>
                            {courses.map((course) => (
                                <option key={course.id} value={course.id}>
                                    {course.title}
                                </option>
                            ))}
                        </select>

                        {/* Section Filter */}
                        <select
                            value={localSection}
                            onChange={(e) => handleSectionChange(e.target.value)}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            disabled={availableSections.length === 0}
                        >
                            <option value="">Todas las secciones</option>
                            {availableSections.map((section) => (
                                <option key={section.id} value={section.id}>
                                    {section.title}
                                </option>
                            ))}
                        </select>

                        {/* Topic Filter */}
                        <select
                            value={localTopic}
                            onChange={(e) => handleTopicChange(e.target.value)}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            disabled={availableTopics.length === 0}
                        >
                            <option value="">Todos los temas</option>
                            {availableTopics.map((topic) => (
                                <option key={topic.id} value={topic.id}>
                                    {topic.title}
                                </option>
                            ))}
                        </select>

                        {/* Status Filter */}
                        <select
                            value={localStatus}
                            onChange={(e) => handleStatusChange(e.target.value)}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                            <option value="">Todos los estados</option>
                            <option value="pending">Pendiente</option>
                            <option value="approved">Aprobado</option>
                            <option value="rejected">No aprobado</option>
                        </select>
                    </div>
                </div>

                {/* Results Count */}
                {pagination.total > 0 && (
                    <div className="mb-4 text-sm text-gray-600">
                        Mostrando {submissions.length} de {pagination.total} entregas
                    </div>
                )}

                {submissions.length === 0 ? (
                    <div className="rounded-lg bg-white p-12 text-center shadow">
                        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                            {filters.search || filters.course || filters.section || filters.topic || filters.status ? (
                                <Search size={32} className="text-gray-400" />
                            ) : (
                                <FileCheck size={32} className="text-gray-400" />
                            )}
                        </div>
                        <h3 className="mb-2 text-lg font-semibold text-gray-900">
                            {filters.search || filters.course || filters.section || filters.topic || filters.status
                                ? 'No se encontraron resultados'
                                : 'No hay entregas de tareas'}
                        </h3>
                        <p className="text-gray-600">
                            {filters.search || filters.course || filters.section || filters.topic || filters.status
                                ? 'Intenta ajustar los filtros de búsqueda.'
                                : 'No hay entregas de tareas en los cursos de tu academia.'}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-hidden rounded-lg bg-white shadow">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                            <button
                                                type="button"
                                                onClick={() => handleSort('student')}
                                                className="inline-flex items-center gap-1"
                                            >
                                                Estudiante
                                                {renderSortIcon('student')}
                                            </button>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                            <button
                                                type="button"
                                                onClick={() => handleSort('assignment')}
                                                className="inline-flex items-center gap-1"
                                            >
                                                Tarea
                                                {renderSortIcon('assignment')}
                                            </button>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                            <button
                                                type="button"
                                                onClick={() => handleSort('course')}
                                                className="inline-flex items-center gap-1"
                                            >
                                                Curso
                                                {renderSortIcon('course')}
                                            </button>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                            <button
                                                type="button"
                                                onClick={() => handleSort('section')}
                                                className="inline-flex items-center gap-1"
                                            >
                                                Sección
                                                {renderSortIcon('section')}
                                            </button>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                            <button
                                                type="button"
                                                onClick={() => handleSort('status')}
                                                className="inline-flex items-center gap-1"
                                            >
                                                Estado
                                                {renderSortIcon('status')}
                                            </button>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                            <button
                                                type="button"
                                                onClick={() => handleSort('created_at')}
                                                className="inline-flex items-center gap-1"
                                            >
                                                Entregado
                                                {renderSortIcon('created_at')}
                                            </button>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                            <button
                                                type="button"
                                                onClick={() => handleSort('reviewed_at')}
                                                className="inline-flex items-center gap-1"
                                            >
                                                Revisado
                                                {renderSortIcon('reviewed_at')}
                                            </button>
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                                            Acciones
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                                    {submissions.map((submission) => (
                                        <tr key={submission.id} className="hover:bg-gray-50">
                                            <td
                                                className="whitespace-nowrap px-6 py-4 cursor-pointer"
                                                onClick={() => {
                                                    window.location.href = route(
                                                        'staff-assignment-submissions-detail',
                                                        { submission_id: submission.id }
                                                    ).toString();
                                                }}
                                            >
                                                <div className="flex items-center">
                                                    <div className="mr-3">
                                                        <Avatar user={submission.student} size="md" />
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900 hover:text-blue-600">
                                                            {submission.student.full_name}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {submission.student.email}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td
                                                className="px-6 py-4 cursor-pointer"
                                                onClick={() => {
                                                    window.location.href = route(
                                                        'staff-assignment-submissions-detail',
                                                        { submission_id: submission.id }
                                                    ).toString();
                                                }}
                                            >
                                                <div className="text-sm text-gray-900 hover:text-blue-600">
                                                    {submission.assignment.title}
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <div className="text-sm text-gray-900">
                                                    {submission.assignment.course.title}
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <div className="text-sm text-gray-900">
                                                    {submission.assignment.section?.title || '-'}
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                {getStatusBadge(submission.status)}
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <div className="text-sm text-gray-900">
                                                    {formatDate(submission.created_at)}
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <div className="text-sm text-gray-900">
                                                    {submission.reviewed_at
                                                        ? formatDate(submission.reviewed_at)
                                                        : '-'}
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        onClick={() => {
                                                            window.location.href = route(
                                                                'staff-assignment-submissions-detail',
                                                                { submission_id: submission.id }
                                                            ).toString();
                                                        }}
                                                        className="text-blue-600 hover:text-blue-900"
                                                        title="Ver detalles"
                                                    >
                                                        <Eye size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            window.location.href = route(
                                                                'staff-assignment-submissions-edit',
                                                                { submission_id: submission.id }
                                                            ).toString();
                                                        }}
                                                        className="text-gray-600 hover:text-gray-900"
                                                        title="Editar"
                                                    >
                                                        <Edit size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {pagination.num_pages > 1 && (
                            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-6 py-4">
                                <div className="flex flex-1 justify-between sm:hidden">
                                    <button
                                        onClick={() => handlePageChange(pagination.previous_page!)}
                                        disabled={!pagination.has_previous}
                                        className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        Anterior
                                    </button>
                                    <button
                                        onClick={() => handlePageChange(pagination.next_page!)}
                                        disabled={!pagination.has_next}
                                        className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        Siguiente
                                    </button>
                                </div>
                                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                                    <div>
                                        <p className="text-sm text-gray-700">
                                            Página <span className="font-medium">{pagination.page}</span> de{' '}
                                            <span className="font-medium">{pagination.num_pages}</span>
                                        </p>
                                    </div>
                                    <div>
                                        <nav
                                            className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                                            aria-label="Pagination"
                                        >
                                            <button
                                                onClick={() => handlePageChange(pagination.previous_page!)}
                                                disabled={!pagination.has_previous}
                                                className="relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                <span className="sr-only">Anterior</span>
                                                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                                            </button>
                                            <button
                                                onClick={() => handlePageChange(pagination.next_page!)}
                                                disabled={!pagination.has_next}
                                                className="relative inline-flex items-center rounded-r-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                <span className="sr-only">Siguiente</span>
                                                <ChevronRight className="h-5 w-5" aria-hidden="true" />
                                            </button>
                                        </nav>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </StaffLayout>
    );
};

const Index: React.FC<AssignmentSubmissionsIndexProps> = (props) => {
    return (
        <ThemeWrapper>
            <IndexContent {...props} />
        </ThemeWrapper>
    );
};

export default Index;
